import * as React from 'react';
import PlatformConditional from 'Components/PlatformConditional';
import { isPartialDate, sortPartialDate } from 'neuro-utils';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import { uhdrsFunctionalAssessment, uhdrsFunctionalCapacity } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType, filterDocumentsByType, sortDocumentsDescending } from 'Utility/documentHandling';
import { fm } from 'Components/FormatMessage';
import type { TFunctionalPerformanceDocumentType } from '../utils';

const getLatestFunctionalAssessment = (
  docs: Array<IDocumentWithTypeV2<TFunctionalPerformanceDocumentType>>,
): { date: PartialDate | undefined; value: React.JSX.Element | string | undefined } | undefined => {
  const latest = filterByDocumentType(docs, 'uhdrsFunctionalAssessment')
    .sort((n1, n2) => sortPartialDate(n2.date, n1.date))
    .at(0);
  if (!latest) return undefined;
  const dateOfLatest = latest.date;
  const valueOfLatest = String(uhdrsFunctionalAssessment.calculateScore(latest) ?? '-');
  return {
    date: dateOfLatest ?? undefined, // dumdum
    value: valueOfLatest,
  };
};

const getLatestIndependenceScale = (
  docs: Array<IDocumentWithTypeV2<TFunctionalPerformanceDocumentType>>,
): { date: PartialDate | undefined; value: React.JSX.Element | string | undefined } | undefined => {
  const latest = filterByDocumentType(docs, 'uhdrsIndependenceScale')
    .sort((n1, n2) => sortPartialDate(n2.date, n1.date))
    .at(0);
  if (!latest) return undefined;
  const dateOfLatest = latest.date;
  const valueOfLatest = latest.value;
  return {
    date: dateOfLatest ?? undefined, // dumdum
    value: valueOfLatest ?? undefined,
  };
};

const getLatestFunctionalCapacity = (
  docs: Array<IDocumentWithTypeV2<TFunctionalPerformanceDocumentType>>,
): { date: PartialDate | undefined; value: React.JSX.Element | string | undefined } | undefined => {
  const latest = filterByDocumentType(docs, 'uhdrsFunctionalCapacity')
    .sort((n1, n2) => sortPartialDate(n2.date, n1.date))
    .at(0);
  if (!latest) return undefined;
  const dateOfLatest = latest.date;
  const valueOfLatest = String(uhdrsFunctionalCapacity.calculateScore(latest) ?? '-');
  return {
    date: dateOfLatest ?? undefined,
    value: valueOfLatest,
  };
};

const getLatestDocsEpilepsy = (
  docs: Array<IDocumentWithTypeV2<TFunctionalPerformanceDocumentType>>,
): Array<IDocumentWithTypeV2<'modifiedRankinScale' | 'modifiedChildrensGlobalAssessmentScale'>> => {
  const sortedArrOfLatest = filterDocumentsByType(docs, [
    'modifiedRankinScale',
    'modifiedChildrensGlobalAssessmentScale',
  ]).sort(sortDocumentsDescending);
  return sortedArrOfLatest;
};

const FunctionalPerformanceDash = ({ documents }: IOwnProps): React.JSX.Element => (
  <React.Fragment>
    <PlatformConditional platform={['huntington']}>
      <TileContentMaker
        type="specificList"
        data={[
          ...(getLatestFunctionalAssessment(documents)
            ? [
                {
                  date: isPartialDate(getLatestFunctionalAssessment(documents)?.date)
                    ? formatPartialDate(getLatestFunctionalAssessment(documents)?.date)
                    : '',
                  title: fm('functionalPerformance.uhdrsFunctionalAssessment'),
                  value: getLatestFunctionalAssessment(documents)?.value,
                  key: 'uhdrsFunctionalAssessment',
                },
              ]
            : []),
          ...(getLatestIndependenceScale(documents)
            ? [
                {
                  date: isPartialDate(getLatestIndependenceScale(documents)?.date)
                    ? formatPartialDate(getLatestIndependenceScale(documents)?.date)
                    : '',
                  title: fm('functionalPerformance.uhdrsIndependenceScale'),
                  value: getLatestIndependenceScale(documents)?.value,
                  key: 'uhdrsIndependenceScale',
                },
              ]
            : []),
          ...(getLatestFunctionalCapacity(documents)
            ? [
                {
                  date: isPartialDate(getLatestFunctionalCapacity(documents)?.date)
                    ? formatPartialDate(getLatestFunctionalCapacity(documents)?.date)
                    : '',
                  title: fm('functionalPerformance.uhdrsFunctionalCapacity'),
                  value: getLatestFunctionalCapacity(documents)?.value,
                  key: 'uhdrsFunctionalCapacity',
                },
              ]
            : []),
        ]}
      />
    </PlatformConditional>
    <PlatformConditional platform="epilepsy">
      <TileContentMaker
        type="recentList"
        data={getLatestDocsEpilepsy(documents).map(d => ({
          date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
          title:
            d._type === 'modifiedRankinScale'
              ? fm('functionalPerformance.modifiedRankinScale')
              : fm('functionalPerformance.modifiedChildrensGlobalAssessmentScaleShort'),
          value: d.rating || d.rating === 0 ? d.rating || '0' : null,
          key: d._id,
        }))}
      />
    </PlatformConditional>
  </React.Fragment>
);

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TFunctionalPerformanceDocumentType>>;
}

export default FunctionalPerformanceDash;
